import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { API_BASE_URL } from '../../../config';

interface Photo {
  id: string;
  original_key: string;
  title: string | null;
  created_at: string;
}

const LogoSvg = ({ size = 32 }: { size?: number }) => (
  <svg viewBox="0 0 60 72" width={size} height={size * 1.2} xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="pubLogo" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#42a5f5"/><stop offset="100%" stopColor="#0d47a1"/></linearGradient></defs>
    <path d="M30 68 C30 68 56 44 56 28 C56 13.6 44.4 2 30 2 C15.6 2 4 13.6 4 28 C4 44 30 68 30 68Z" fill="url(#pubLogo)"/>
    <circle cx="30" cy="26" r="14" fill="white"/>
    <circle cx="30" cy="26" r="13" fill="none" stroke="#1565c0" strokeWidth="1.4"/>
    <ellipse cx="30" cy="26" rx="5.5" ry="13" fill="none" stroke="#1565c0" strokeWidth="1"/>
    <line x1="17" y1="26" x2="43" y2="26" stroke="#1565c0" strokeWidth="1"/>
  </svg>
);

const promos = [
  {
    bg: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)',
    icon: '🗺️',
    title: 'Seu mapa pessoal',
    sub: 'Marque cada canto do mundo que você visitou',
  },
  {
    bg: 'linear-gradient(135deg, #1565c0 0%, #90caf9 100%)',
    icon: '🛡️',
    title: 'Fotos protegidas',
    sub: 'Suas memórias seguras e organizadas por local',
  },
  {
    bg: 'linear-gradient(135deg, #0d47a1 0%, #1e88e5 100%)',
    icon: '🏆',
    title: 'Troféus de viagem',
    sub: 'Conquiste badges visitando países e maravilhas',
  },
  {
    bg: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)',
    icon: '🔒',
    title: 'Privacidade total',
    sub: 'Compartilhe apenas com quem você escolher',
  },
];

export function PublicGalleryPage() {
  const { t } = useTranslation();
  const { shareToken } = useParams<{ shareToken: string }>();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const pageRef = React.useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    async function fetchGallery() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/photos/gallery/${shareToken}?page=1&limit=20`);
        if (!res.ok) { setError(true); return; }
        const data = await res.json();
        setDisplayName(data.displayName || '');
        setTotalPhotos(data.total || 0);
        setPhotos(data.photos || []);
        setHasMore(data.hasMore || false);
      } catch (err) {
        console.error('Failed to fetch public gallery:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    if (shareToken) fetchGallery();
  }, [shareToken]);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;
      const scrollBottom = window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollBottom >= docHeight - 300) {
        setLoadingMore(true);
        pageRef.current += 1;
        fetch(`${API_BASE_URL}/api/v1/photos/gallery/${shareToken}?page=${pageRef.current}&limit=20`)
          .then(r => r.json())
          .then(data => {
            setPhotos(prev => [...prev, ...(data.photos || [])]);
            setHasMore(data.hasMore || false);
          })
          .finally(() => setLoadingMore(false));
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore, shareToken]);

  if (loading) {
    return (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}><CircularProgress /></Box>);
  }

  if (error) {
    return (<Box sx={{ textAlign: 'center', mt: 10 }}><Typography variant="h6" color="text.secondary">{t('common.error')}</Typography></Box>);
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Top Header with Logo */}
      <Box sx={{ background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #42a5f5 100%)', color: 'white', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LogoSvg size={28} />
          <Typography variant="h6" fontWeight={700}>Globe Pinned</Typography>
        </Box>
        <Button variant="contained" size="small" href="/auth/register" sx={{ bgcolor: 'white', color: '#0d47a1', fontWeight: 700, '&:hover': { bgcolor: '#e3f2fd' } }}>
          Criar conta grátis
        </Button>
      </Box>

      {/* User Header */}
      <Box sx={{ textAlign: 'center', py: 3, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h4" fontWeight={700} color="#1a237e">
          {displayName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {totalPhotos} {t('globe.photos')}
        </Typography>
      </Box>

      {/* Photo Grid */}
      <Box sx={{ p: 1, maxWidth: 1200, mx: 'auto' }}>
        {photos.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <PhotoLibraryIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography color="text.secondary" sx={{ mt: 1 }}>{t('gallery.empty')}</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
            {photos.map((photo, idx) => {
              const url = photo.url || `/uploads/${photo.original_key.split('/').pop()}`;
              const items: React.ReactNode[] = [];

              // Promo banner every 8 photos
              if (idx > 0 && idx % 8 === 0) {
                const promo = promos[(idx / 8 - 1) % promos.length];
                items.push(
                  <Box key={'promo-' + idx} sx={{ background: promo.bg, borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 1.5, aspectRatio: '1', gridColumn: 'span 1', gridRow: 'span 1', position: 'relative', overflow: 'hidden' }}>
                    {/* Background pattern */}
                    <Box sx={{ position: 'absolute', inset: 0, opacity: 0.08 }}>
                      <svg viewBox="0 0 100 100" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                        <path d="M50 90 C50 90 80 65 80 50 C80 35 66 22 50 22 C34 22 20 35 20 50 C20 65 50 90 50 90Z" fill="white"/>
                      </svg>
                    </Box>
                    {/* Logo mini */}
                    <Box sx={{ position: 'absolute', top: 8, left: 8 }}><LogoSvg size={40} /></Box>
                    <Typography sx={{ fontSize: '3.9rem', mb: 0.5 }}>{promo.icon}</Typography>
                    <Typography variant="caption" fontWeight={800} sx={{ color: 'white', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1 }}>Globe Pinned</Typography>
                    <Typography variant="caption" fontWeight={700} textAlign="center" sx={{ color: 'white', fontSize: '0.75rem', mt: 0.3 }}>{promo.title}</Typography>
                    <Typography variant="caption" textAlign="center" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.6rem' }}>{promo.sub}</Typography>
                    <Button size="small" href="/auth/register" sx={{ mt: 0.5, fontSize: '0.55rem', py: 0.2, px: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>Comece agora</Button>
                  </Box>
                );
              }

              // Zig-zag layout
              const groupIdx = Math.floor(idx / 5);
              const posInGroup = idx % 5;
              const isLarge = posInGroup === 0;
              const isOddGroup = groupIdx % 2 === 0;
              const largeCol = isOddGroup ? '1 / 3' : '3 / 5';

              items.push(
                <Box key={photo.id} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', aspectRatio: '1', ...(isLarge ? { gridColumn: largeCol, gridRow: 'span 2' } : {}) }}>
                  <img src={url} alt={photo.title || ''} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  {photo.title && (
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.6))', p: 1 }}>
                      <Typography variant="caption" color="white" noWrap>{photo.title}</Typography>
                    </Box>
                  )}
                </Box>
              );
              return items;
            })}
          </Box>
        )}

        {loadingMore && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress size={24} /></Box>}
      </Box>

      {/* Footer CTA */}
      <Box sx={{ mt: 4, p: 4, background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #42a5f5 100%)', textAlign: 'center', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}><LogoSvg size={40} /></Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          Crie seu próprio porta retratos
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mb: 3, maxWidth: 500, mx: 'auto' }}>
          Organize suas fotos de viagem em um mapa interativo. Compartilhe apenas com quem você quiser. Suas memórias protegidas.
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" fontWeight={600}>🗺️ Mapa interativo</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Marque seus locais</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" fontWeight={600}>📷 Fotos seguras</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Armazenamento protegido</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" fontWeight={600}>🔒 Privacidade</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Você decide quem vê</Typography>
          </Box>
        </Box>
        <Button variant="contained" size="large" href="/auth/register" sx={{ bgcolor: 'white', color: '#0d47a1', fontWeight: 700, px: 4, '&:hover': { bgcolor: '#e3f2fd' } }}>
          Criar minha conta grátis →
        </Button>
      </Box>

      {/* Bottom brand */}
      <Box sx={{ textAlign: 'center', py: 2, bgcolor: '#0d47a1' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <LogoSvg size={16} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Globe Pinned — Suas memórias, seu mapa, sua privacidade.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
